import type {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
} from '@apollo/client';
import { useEffect, useRef } from 'react';
import { logger } from '../../logger';
import { precacheOperation } from '../precache/precacheOperation';
import { useQuery } from '@apollo/client';
import { getPrecacheOperationKey } from '../precache/getPrecacheOperationKey';

const DEFAULT_REFETCH_DELAY = 5000;

const refetchedOperations = new Set<string>();

export const usePrecacheQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
  precacheOptions?: {
    refetchDelay?: number;
    precacheEnabled?: boolean;
    onRefetch?: (newData: TData, oldData: TData | undefined) => void | Promise<void>;
  }
): QueryResult<TData, TVariables> => {
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Operation key of the currently scheduled (not-yet-fired) refetch, so that
  // cancelling the timeout can also un-mark the operation as refetched.
  const scheduledOperationKeyRef = useRef<string>();

  // Clear any pending precache refetch when the consumer unmounts. Otherwise the
  // delayed refetch fires against a torn-down component — leaking work in
  // production and, in tests, triggering React state updates outside act() after
  // the test has finished (a cascade of act() warnings across the suite).
  useEffect(
    () => () => {
      if (refetchTimeoutRef.current === undefined) return;
      clearTimeout(refetchTimeoutRef.current);
      refetchTimeoutRef.current = undefined;
      // The refetch never ran, so allow it to be scheduled again on a later
      // mount rather than leaving the operation permanently marked as refetched.
      if (scheduledOperationKeyRef.current !== undefined) {
        refetchedOperations.delete(scheduledOperationKeyRef.current);
        scheduledOperationKeyRef.current = undefined;
      }
    },
    []
  );

  const result = useQuery<TData, TVariables>(query, {
    ...options,
    context: {
      ...options?.context,
      // if there aren't any enabled precache flags, we can consider
      // the precache to be 'off', and not store anything there even
      // from "normal" query calls. This helps be more "zero-footprint",
      // but also our metrics check the cache for the data to mark
      // whether it was precached or not, so we don't want to add stuff
      // that could get picked up on a page refresh
      precachePolicy: precacheOptions?.precacheEnabled === true ? 'cache-first' : 'network-only',
    },
    onCompleted: (...args) => {
      options?.onCompleted?.(...args);
      const beforeData = args[0] ?? result.data;
      // Give 5 seconds to the Viewer to load, after that we can attempt to
      // fetch fresh data from the server and in case refresh the Viewer to
      // reflect any changes.
      // We want to delay this operation because the fetch operation can affect
      // the performance of the Viewer.
      // We can consider this a good trade-off to ensure users don't see stale
      // data for too long.
      const operationKey = getPrecacheOperationKey(query, options?.variables);

      // If we already refetched this operation, we don't need to do it again
      if (refetchedOperations.has(operationKey)) {
        logger.debug('[usePrecacheQuery] operation already refetched, skipping refetch');
        return;
      }

      // Cancel any earlier pending refetch so only one is in flight per hook
      // instance (e.g. if onCompleted runs again for changed variables). The
      // cancelled refetch never ran, so un-mark its operation too — mirroring the
      // unmount cleanup. Otherwise the old key is orphaned in refetchedOperations
      // forever and silently blocks rescheduling it on a later mount.
      if (refetchTimeoutRef.current !== undefined) {
        clearTimeout(refetchTimeoutRef.current);
        refetchTimeoutRef.current = undefined;
        if (scheduledOperationKeyRef.current !== undefined) {
          refetchedOperations.delete(scheduledOperationKeyRef.current);
          scheduledOperationKeyRef.current = undefined;
        }
      }

      // Mark this operation as refetched so we don't do it again in the future
      refetchedOperations.add(operationKey);
      scheduledOperationKeyRef.current = operationKey;

      logger.debug('[usePrecacheQuery] refetching operation scheduled');
      refetchTimeoutRef.current = setTimeout(async () => {
        refetchTimeoutRef.current = undefined;
        scheduledOperationKeyRef.current = undefined;

        logger.debug('[usePrecacheQuery] refetching operation (precache)');

        await precacheOperation(query, options?.variables, {
          refetch: true,
          context: options?.context,
        });

        logger.debug('[usePrecacheQuery] refetching operation (Apollo refetch)');
        const refetchResult = await result.refetch();

        try {
          await precacheOptions?.onRefetch?.(refetchResult.data, beforeData);
        } catch (e) {
          logger.error('[usePrecacheQuery] onRefetch callback error', { error: e });
        }
      }, precacheOptions?.refetchDelay ?? DEFAULT_REFETCH_DELAY);
    },
  });
  return result;
};
